import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePopoverPosition } from '@/hooks/usePopoverPosition'
import { useClickOutside } from '@/hooks/useClickOutside'
import Select from '@/components/common/Select'
import Icon from '@/components/common/Icon'
import { useTranslation } from 'react-i18next'
import useSpeechDialogStore from '@/components/SpeechDialog/speech-dialog.store.ts'
import { getInputDevices } from '@/features/speech/domain/use-cases/get-input-devices'
import Switch from '@/components/common/Switch'

interface SettingsPopoverProps {
  anchorRef: React.RefObject<HTMLDivElement>
  onClose: () => void
  open: boolean
}

const SettingsPopover: React.FC<SettingsPopoverProps> = ({
  anchorRef,
  onClose,
  open,
}) => {
  const {
    countIn,
    setCountIn,
    stereo,
    setStereo,
    autoGain,
    setAutoGain,
    noiseSuppression,
    setNoiseSuppression,
    echoCancel,
    setEchoCancel,
  } = useSpeechDialogStore()
  const { t } = useTranslation()
  const [inputDevices, setInputDevices] = useState<
    { deviceId: string; label: string }[]
  >([])
  const [selectedDevice, setSelectedDevice] = useState<string | undefined>()
  const popoverRef = useRef<HTMLDivElement>(null)
  const style = usePopoverPosition(anchorRef, open)

  const countInOptions = [3, 5].map((v) => ({
    value: v.toString(),
    label: `${v}s`,
  }))
  const inputDevicesOptions = inputDevices.map((d) => ({
    value: d.deviceId,
    label: d.label,
  }))

  const currentCountInOption =
    countInOptions.find((opt) => Number.parseInt(opt.value, 10) === countIn) ||
    countInOptions[0]

  useClickOutside({
    itemRef: popoverRef,
    triggerRef: popoverRef,
    setIsOpen: (v: boolean) => {
      if (v === false) onClose()
    },
    isOpen: open,
  })

  // Load input devices when popover opens
  useEffect(() => {
    if (!open) return
    const fx = async (): Promise<void> => {
      const inputDevices = await getInputDevices()
      setInputDevices(inputDevices)
      if (
        inputDevices.length &&
        (!selectedDevice ||
          !inputDevices.some((d) => d.deviceId === selectedDevice))
      ) {
        setSelectedDevice(inputDevices[0]?.deviceId)
      }
    }
    fx()
  }, [open, selectedDevice])

  return createPortal(
    <div
      ref={popoverRef}
      style={style}
      className="flex flex-col gap-6 rounded-lg border border-zinc-700 bg-neutral-900 p-5 shadow-lg"
    >
      <div className="inline-flex items-center justify-center gap-2 self-stretch">
        <div className="flex-1 justify-start text-sm font-medium leading-tight text-white">
          Settings
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-5 w-5 items-center justify-center text-zinc-400 hover:text-white"
        >
          <Icon name="xMark" />
        </button>
      </div>
      <div className="inline-flex flex-col items-start justify-start gap-5 self-stretch">
        <div className="flex w-full flex-col gap-3">
          <div className="text-xs font-medium text-zinc-300">
            {t('ls_speech_dialog_settings_input_device')}
          </div>
          <Select
            filterNameKey="ls_speech_dialog_settings_input_device"
            options={inputDevicesOptions}
            defaultOption={inputDevicesOptions[0]}
            onChange={setSelectedDevice}
            translateLabel={false}
          />
          <div className="mt-2 text-xs font-medium text-zinc-300">
            {t('ls_speech_dialog_settings_count_in')}
          </div>
          <Select
            filterNameKey="ls_speech_dialog_settings_count_in"
            options={countInOptions}
            defaultOption={currentCountInOption}
            onChange={(val) => setCountIn(val ? Number.parseInt(val, 10) : 3)}
            translateLabel={false}
          />
        </div>
        <div className="inline-flex flex-col items-start justify-start gap-4 self-stretch">
          <div className="text-sm font-medium text-white">Audio Options</div>
          <div className="mb-1 w-full border-b border-zinc-700" />
          <div className="flex flex-col gap-5">
            <div className="inline-flex h-5 items-center gap-2">
              <Switch checked={stereo} onCheckedChange={setStereo} />
              <div className="justify-start text-xs font-semibold leading-none text-white">
                Record Stereo Audio
              </div>
            </div>
            <div className="inline-flex h-5 items-center gap-2">
              <Switch checked={autoGain} onCheckedChange={setAutoGain} />
              <div className="justify-start text-xs font-semibold leading-none text-white">
                Auto Gain Control
              </div>
            </div>
            <div className="inline-flex h-5 items-center gap-2">
              <Switch
                checked={noiseSuppression}
                onCheckedChange={setNoiseSuppression}
              />
              <div className="justify-start text-xs font-semibold leading-none text-white">
                Noise Suppression
              </div>
            </div>
            <div className="inline-flex h-5 items-center gap-2">
              <Switch checked={echoCancel} onCheckedChange={setEchoCancel} />
              <div className="justify-start text-xs font-semibold leading-none text-white">
                Echo Cancelation
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default SettingsPopover
